import { Intent } from "lib/common/enums";
import useCheckUsername from "lib/hooks/register/useCheckUsername";
import useOnboardReqWithTempToken from "lib/hooks/register/useOnboardReqWithTempToken";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import Button from "../atomic/Button";
import TextField from "../atomic/TextField";
import ChooseAvatar from "../chooseAvatar";
import UploadAvatar from "../chooseAvatar/UploadAvatar";
import { Form } from "../Form";
import FormControl from "../Form/FormControl";
import Spacer from "../Form/Spacer";

/**
 * AvatarField acts as a bridge between FormControl and your Avatar components.
 * It receives injected props (value, onChange, intent, etc.) from FormControl
 * and mocks an HTML event so FormControl can track the avatar data.
 */
const AvatarField = ({
  value,
  onChange,
  label,
  message,
  intent,
  required,
}: any) => {
  const handleSelect = (avatarData: any) => {
    if (onChange) {
      // FormControl expects an event-like object, so we mock it
      onChange({
        target: {
          value: avatarData,
          type: "custom",
          className: "avatarItem", // Avoids checkbox/select logic in FormControl
        },
      });
    }
  };

  const hasError = intent === Intent.ERROR;

  return (
    <div className="flex flex-col">
      {/* Label */}
      {label && (
        <span className="font-semibold text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </span>
      )}

      {/* Avatar Selector */}
      <div className={hasError ? "ring-2 ring-red-400 rounded-xl p-2" : ""}>
        {/* Pass handleSelect to whatever prop ChooseAvatar/UploadAvatar uses to pass back data (e.g. onChange, onUpload) */}
        <ChooseAvatar
          className="w-full"
          relative={false}
          onChange={(e: any) => handleSelect(e.target.value)}
        >
          <UploadAvatar onUpload={handleSelect} />
        </ChooseAvatar>
      </div>

      {/* Error Message */}
      {hasError && message && (
        <p className="mt-1.5 text-sm text-red-500">{message}</p>
      )}
    </div>
  );
};

interface SecurityProps {
  nextTab?: () => void;
}

function Security({ nextTab }: SecurityProps) {
  const router = useRouter();
  const user = router.query?.user;

  const { reqWithTempToken, success } = useOnboardReqWithTempToken();
  const { checkUser, validUsername } = useCheckUsername();

  const [passwordValue, setPasswordValue] = useState("");
  const [passwordValidation, setPasswordValidation] = useState(/^$/);
  const [userName, setUserName] = useState("");

  useEffect(() => {
    if (userName.length > 0) {
      const debounceFn = setTimeout(() => {
        checkUser({ username: userName });
      }, 1000);

      return () => clearTimeout(debounceFn);
    }
  }, [userName, checkUser]);

  useEffect(() => {
    if (nextTab && success) {
      nextTab();
    }
  }, [nextTab, success]);

  return (
    <div className="py-8 max-w-screen-sm">
      <Form
        onSubmit={(value) => {
          // No manual avatar state checks needed!
          // `userAvatarPicture` is now automatically tracked and injected into the `value` payload.
          reqWithTempToken("set_password", value);
        }}
      >
        {/* Basic Information */}
        <div>
          <h6 className="text-gray-700">Basic Information</h6>

          <p className="text-gray text-sm">
            Choose a profile avatar or upload your own photo. Required to
            complete registration.
          </p>
        </div>

        <Spacer className="flex-1 py-6">
          <FormControl
            name="userAvatarPicture"
            label="Profile Picture"
            required
            defaultValue="other_4.svg"
            validationRules={[
              {
                rule: /.+/,
                message: "Please select or upload a profile picture to continue.",
                intent: Intent.ERROR,
              },
            ]}
          >
            <AvatarField />
          </FormControl>

          <FormControl
            className="mt-5"
            name="username"
            label="Username"
            required
            validationRules={[
              {
                rule: /^.{6,}$/,
                message: "Username should be of more than 6 digit",
                intent: Intent.ERROR,
              },
            ]}
          >
            <TextField
              stateChange={(e) => setUserName(e.target.value)}
              type="text"
              infoIcon
              className="rounded bg-gray-50"
              customIntent={
                userName.length > 5 &&
                validUsername &&
                !validUsername?.isAvailable
                  ? Intent.ERROR
                  : undefined
              }
              customMessage={
                userName.length > 5 &&
                validUsername &&
                !validUsername?.isAvailable
                  ? "The username must be between 6 and 20 characters. The username can only contain alphanumeric characters (A-Z, a-z, 0-9), periods (.), and underscores (_). No consecutive periods or underscores. The username cannot start or end with a period or underscore."
                  : undefined
              }
            />
          </FormControl>
        </Spacer>

        {/* Security */}
        <Spacer id="security" className="flex flex-col pb-5">
          <div className="mb-5 sm:mb-0 flex-1">
            <h6 className="text-gray-700">Security</h6>

            <p className="text-gray text-sm">
              Set a strong password. Make sure your password is longer than 8
              digits.
            </p>
          </div>

          <Spacer className="flex-1">
            <FormControl
              className="mb-5"
              name="password1"
              label="Password"
              validationRules={[
                {
                  rule: /^.{8,}$/,
                  message: "The password must be at least 8 digit long",
                  intent: Intent.ERROR,
                },
              ]}
              required
            >
              <TextField
                stateChange={(e) => {
                  const password = e.target.value;

                  setPasswordValue(password);
                  setPasswordValidation(
                    new RegExp(
                      `^${password.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`,
                    ),
                  );
                }}
                type="password"
                className="bg-gray-50 rounded"
              />
            </FormControl>

            <FormControl
              className="mb-5"
              name="password2"
              label="Confirm Password"
              validationRules={[
                {
                  rule: passwordValidation,
                  message: "The password do not match",
                  intent: Intent.ERROR,
                },
              ]}
              required
            >
              <TextField type="password" className="bg-gray-50 rounded" />
            </FormControl>

            <Spacer>
              <Button type="submit" block>
                Continue
              </Button>
            </Spacer>
          </Spacer>
        </Spacer>
      </Form>
    </div>
  );
}

export default Security;
